import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Card } from 'primereact/card';
import * as echarts from 'echarts';
import apiClient from '../../../stateManagers/services/axiosInterceptor';

// =====================================================================================
// API CONTRACT NOTES
// =====================================================================================
// This card now talks to TWO endpoints. One already exists; one is new and needs a
// backend counterpart. Both follow the same { status, message, data } envelope and
// ?period=today|week query convention already used by /api/staff-attendance-stats,
// so the Go handler for the new one should be a straightforward sibling of it.
//
// 1) GET /api/staff-attendance-stats?period=&role=            (EXISTING — extended)
//    `ScanTypeStats` gains one field: `unmarked`. This is staff who were scheduled/
//    expected to scan but have no attendance record for the day at all — distinct
//    from `absent` (which presumably means explicitly marked absent). Assumption:
//    total = present + absent + late + unmarked. Flag to backend if that's not how
//    `total` is currently computed, since the bar chart below stacks all four to
//    equal the bar's full height.
//
// 2) GET /api/class-coverage-stats?period=&role=               (NEW — needs building)
//    Answers "how many classes have their assigned teacher absent today" — a simple
//    attendance-based rollup, NOT yet aware of whether a substitute was arranged.
//    Substitute/arrangement checking is deferred to a later pass; for now every
//    absent teacher's classes count as "affected", full stop. Response:
//      {
//        status: "success",
//        data: {
//          period: "today" | "week",
//          start_date, end_date, role_filter,
//          total_classes: number,
//          unaffected_classes: number,          // assigned teacher present (or late)
//          affected_classes: number,            // assigned teacher marked absent
//          pending_confirmation_classes: number // assigned teacher's attendance not marked yet — coverage unknown
//        }
//      }
//    `pending_confirmation_classes` is the "yellow" bucket. Went with this name over
//    "unmarked_classes" because it describes the action needed (someone needs to
//    confirm the teacher's attendance before the class's status is even knowable),
//    not just the raw data state.
// =====================================================================================

// ==================== TYPES ====================
interface ScanTypeStats {
  present: number;
  absent: number;
  late: number;
  unmarked: number;
  total: number;
}

interface RoleStats {
  checkin: ScanTypeStats;
  checkout: ScanTypeStats;
}

interface StaffAttendanceStatsData {
  period: 'today' | 'week';
  start_date: string;
  end_date: string;
  role_filter: string | null;
  summary: {
    checkin: ScanTypeStats;
    checkout: ScanTypeStats;
  };
  by_role: Record<string, RoleStats>;
}

interface StaffAttendanceStatsResponse {
  status: string;
  message: string;
  data: StaffAttendanceStatsData;
}

interface ClassCoverageStats {
  period: 'today' | 'week';
  start_date: string;
  end_date: string;
  role_filter: string | null;
  total_classes: number;
  unaffected_classes: number;
  affected_classes: number;
  pending_confirmation_classes: number;
}

interface ClassCoverageResponse {
  status: string;
  message: string;
  data: ClassCoverageStats;
}

type PeriodKey = 'today' | 'week';
type RoleKey = '' | 'teacher' | 'principal' | 'staff' | 'admin' | 'superadmin';
type ScanType = 'checkin' | 'checkout';

const PERIOD_LABELS: Record<PeriodKey, string> = {
  today: 'Today',
  week: 'Week',
};

const ROLE_OPTIONS: { key: RoleKey; label: string }[] = [
  { key: '', label: 'All Roles' },
  { key: 'teacher', label: 'Teacher' },
  { key: 'principal', label: 'Principal' },
  { key: 'staff', label: 'Staff' },
];

const ROLE_LABELS: Record<string, string> = {
  teacher: 'Teacher',
  principal: 'Principal',
  staff: 'Staff',
  admin: 'Admin',
  superadmin: 'Superadmin',
  unspecified: 'Other',
};

// Only these roles are ever shown as bars — matches ROLE_OPTIONS above,
// which deliberately excludes Admin/Superadmin from this dashboard card.
// Filtered again below (not just used as a fallback list) so that even if
// the API's by_role happens to include an admin/superadmin bucket, it
// still never renders here.
const CANONICAL_ROLES = ['teacher', 'principal', 'staff'];

// Same status-segment recipe as UnmarkedClassesChart's Present/Absent/Leave/Skipped
// segments — light→saturated idle, saturated→dark hover, matching soft shadow — now
// with a fourth "unmarked" segment for staff whose attendance simply has no entry.
interface SegmentStyle {
  idleGradient: string;
  hoverGradient: string;
  idleShadow: string;
  hoverShadow: string;
  textColor: string;
  label: string;
}

const SEGMENT_ORDER = ['present', 'late', 'unmarked', 'absent'] as const;
type SegmentKey = (typeof SEGMENT_ORDER)[number];

const SEGMENT_STYLE: Record<SegmentKey, SegmentStyle> = {
  present: {
    idleGradient: 'linear-gradient(180deg, #bbf7d0 0%, #4ade80 100%)',
    hoverGradient: 'linear-gradient(180deg, #4ade80 0%, #15803d 100%)',
    idleShadow: '0 3px 8px rgba(34, 197, 94, 0.2)',
    hoverShadow: '0 10px 18px rgba(21, 128, 61, 0.4)',
    textColor: '#16a34a',
    label: 'Present',
  },
  late: {
    idleGradient: 'linear-gradient(180deg, #fde68a 0%, #f59e0b 100%)',
    hoverGradient: 'linear-gradient(180deg, #f59e0b 0%, #b45309 100%)',
    idleShadow: '0 3px 8px rgba(245, 158, 11, 0.2)',
    hoverShadow: '0 10px 18px rgba(180, 83, 9, 0.4)',
    textColor: '#b45309',
    label: 'Late',
  },
  // Deliberately a cool slate, not a warm amber/red — this is a neutral "no record
  // yet" state, not a bad outcome the way absent/late are.
  unmarked: {
    idleGradient: 'linear-gradient(180deg, #e2e8f0 0%, #94a3b8 100%)',
    hoverGradient: 'linear-gradient(180deg, #94a3b8 0%, #475569 100%)',
    idleShadow: '0 3px 8px rgba(100, 116, 139, 0.18)',
    hoverShadow: '0 10px 18px rgba(71, 85, 105, 0.4)',
    textColor: '#475569',
    label: 'Unmarked',
  },
  absent: {
    idleGradient: 'linear-gradient(180deg, #fecaca 0%, #f87171 100%)',
    hoverGradient: 'linear-gradient(180deg, #f87171 0%, #b91c1c 100%)',
    idleShadow: '0 3px 8px rgba(239, 68, 68, 0.18)',
    hoverShadow: '0 10px 18px rgba(185, 28, 28, 0.4)',
    textColor: '#dc2626',
    label: 'Absent',
  },
};

// Coverage donut segments — now rendered with ECharts (see ClassCoverageDonut),
// using the same linear-gradient-per-slice recipe as FeesSummary's
// EChartsPieChart, rather than the earlier hand-rolled SVG ring. Green/yellow/
// red kept deliberately (same "good / needs a look / bad" grammar as the bar
// chart above), with hues distinct from the bar segments so the two charts
// never look like they're double-counting.
const COVERAGE_STYLE = {
  unaffected: {
    label: 'Unaffected',
    solid: '#22c55e',
    colorStops: [
      { offset: 0, color: '#4ade80' },
      { offset: 1, color: '#16a34a' },
    ],
  },
  pending: {
    label: 'Pending Confirmation',
    solid: '#eab308',
    colorStops: [
      { offset: 0, color: '#fbbf24' },
      { offset: 1, color: '#b45309' },
    ],
  },
  affected: {
    label: 'Affected',
    solid: '#ef4444',
    colorStops: [
      { offset: 0, color: '#f87171' },
      { offset: 1, color: '#b91c1c' },
    ],
  },
} as const;
type CoverageKey = keyof typeof COVERAGE_STYLE;

// ==================== SELF-CONTAINED HOOKS ====================
// Each hook fetches directly from the API — this component owns its own data end
// to end, it is never handed stats via props.
const useStaffAttendanceStats = (period: PeriodKey, role: RoleKey) => {
  const [data, setData] = useState<StaffAttendanceStatsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = { period };
      if (role) params.role = role;

      const response = await apiClient.get<StaffAttendanceStatsResponse>('/api/staff-attendance-stats', { params });
      setData(response.data.data);
    } catch (err: any) {
      console.error('Failed to fetch staff attendance stats:', err);
      setError(err.response?.data?.message || 'Failed to load attendance stats.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [period, role]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { data, loading, error, refetch: fetchStats };
};

const useClassCoverageStats = (period: PeriodKey, role: RoleKey) => {
  const [data, setData] = useState<ClassCoverageStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCoverage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = { period };
      if (role) params.role = role;

      const response = await apiClient.get<ClassCoverageResponse>('/api/class-coverage-stats', { params });
      setData(response.data.data);
    } catch (err: any) {
      console.error('Failed to fetch class coverage stats:', err);
      setError(err.response?.data?.message || 'Failed to load class coverage.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [period, role]);

  useEffect(() => {
    fetchCoverage();
  }, [fetchCoverage]);

  return { data, loading, error, refetch: fetchCoverage };
};

// ==================== ROLE STATUS BARS (UnmarkedClassesChart pattern) ====================
// One growing, stacked column bar per role — segments for Present/Late/Unmarked/
// Absent, hover tooltip with the exact breakdown, grow-from-bottom mount animation.
// Roles with zero records for the period render as a flat grey placeholder bar
// instead of being hidden, so "no data yet" reads as its own state, not an absence
// of UI.
const RoleStatusBars: React.FC<{
  byRole: Record<string, RoleStats>;
  scanType: ScanType;
  roleFilter: RoleKey;
  animated: boolean;
}> = ({ byRole, scanType, roleFilter, animated }) => {
  // Captures the hovered bar's own getBoundingClientRect() so the tooltip
  // can be portaled to document.body and positioned with `fixed` coords —
  // see the comment above the portal render below for why that's
  // necessary rather than an inline absolutely-positioned tooltip.
  const [hovered, setHovered] = useState<{ role: string; rect: DOMRect } | null>(null);

  // If a role filter is active, only that role gets a bar. Otherwise every
  // canonical role gets one, whether or not the API actually reported it —
  // that's the "grey when there's no data" case. Filtered against
  // CANONICAL_ROLES a second time so an admin/superadmin bucket coming
  // back from the API (the backend endpoint itself doesn't restrict which
  // roles it CAN return) still never renders on this card.
  const roleKeys = roleFilter
    ? [roleFilter]
    : Array.from(new Set([...CANONICAL_ROLES, ...Object.keys(byRole)])).filter((r) =>
        CANONICAL_ROLES.includes(r)
      );

  const entries = roleKeys.map((role) => {
    const stats = byRole[role]?.[scanType];
    return {
      role,
      present: stats?.present ?? 0,
      late: stats?.late ?? 0,
      unmarked: stats?.unmarked ?? 0,
      absent: stats?.absent ?? 0,
      total: stats?.total ?? 0,
    };
  });

  if (entries.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: 12, fontWeight: 500 }}>
        No {scanType === 'checkin' ? 'check-ins' : 'check-outs'} in this breakdown.
      </div>
    );
  }

  const CHART_HEIGHT = 120;
  const NO_DATA_HEIGHT = 14;
  const MIN_SEGMENT = 3;
  const BAR_WIDTH = 34;
  const maxTotal = Math.max(...entries.map((e) => e.total), 1);

  const buildSegments = (e: { present: number; late: number; unmarked: number; absent: number }) =>
    SEGMENT_ORDER.map((key) => ({
      key,
      value: e[key],
      ...SEGMENT_STYLE[key],
    }));

  const hoveredEntry = hovered ? entries.find((e) => e.role === hovered.role) ?? null : null;
  const hoveredSegments = hoveredEntry ? buildSegments(hoveredEntry) : [];
  const hoveredHasData = !!hoveredEntry && hoveredEntry.total > 0;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: '22px',
        overflowX: 'auto',
        overflowY: 'visible',
        paddingTop: '20px',
        paddingBottom: '10px',
        justifyContent: entries.length <= 4 ? 'center' : 'flex-start',
      }}
    >
      {entries.map((e, index) => {
        const key = e.role;
        const isHovered = hovered?.role === key;
        const hasData = e.total > 0;
        const total = Math.max(e.total, 1);
        const barCapacity = (total / maxTotal) * CHART_HEIGHT;

        const segments = buildSegments(e);
        const finalHeights = segments.map((s) =>
          Math.max(s.value > 0 ? MIN_SEGMENT : 0, (s.value / total) * barCapacity)
        );
        const heights = finalHeights.map((h) => (animated ? h : 0));

        return (
          <div
            key={key}
            style={{
              position: 'relative',
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: BAR_WIDTH + 16,
            }}
            onMouseEnter={(ev) => setHovered({ role: key, rect: ev.currentTarget.getBoundingClientRect() })}
            onMouseLeave={() => setHovered(null)}
          >

            {hasData ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  height: CHART_HEIGHT,
                  width: BAR_WIDTH,
                  transform: isHovered ? 'scale(1.08)' : 'scale(1)',
                  transformOrigin: 'bottom center',
                  transition: 'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                }}
              >
                {(() => {
                  const renderOrder = [...segments].reverse();
                  const renderHeights = renderOrder.map((_, sIndex) => heights[segments.length - 1 - sIndex]);
                  const renderFinalHeights = renderOrder.map((_, sIndex) => finalHeights[segments.length - 1 - sIndex]);
                  const topVisibleIdx = renderFinalHeights.findIndex((h) => h > 0);
                  let bottomVisibleIdx = -1;
                  for (let i = renderFinalHeights.length - 1; i >= 0; i--) {
                    if (renderFinalHeights[i] > 0) {
                      bottomVisibleIdx = i;
                      break;
                    }
                  }

                  return renderOrder.map((s, sIndex) => {
                    const h = renderHeights[sIndex];
                    const isTopVisible = sIndex === topVisibleIdx;
                    const isBottomVisible = sIndex === bottomVisibleIdx;
                    const showDivider = h > 0 && !isBottomVisible;
                    return (
                      <div
                        key={s.key}
                        style={{
                          width: '100%',
                          height: `${h}px`,
                          background: isHovered ? s.hoverGradient : s.idleGradient,
                          borderRadius: h > 0
                            ? `${isTopVisible ? '8px 8px' : '0 0'} ${isBottomVisible ? '6px 6px' : '0 0'}`
                            : 0,
                          borderBottom: showDivider ? '2px solid #ffffff' : 'none',
                          boxShadow: h > 0 ? (isHovered ? s.hoverShadow : s.idleShadow) : 'none',
                          transition: `height 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) ${index * 0.05 + sIndex * 0.03}s, background 0.25s ease, box-shadow 0.25s ease`,
                        }}
                      />
                    );
                  });
                })()}
              </div>
            ) : (
              // No-data placeholder: a short, flat, dashed-looking grey bar rather
              // than an empty gap where a bar should be.
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  height: CHART_HEIGHT,
                  width: BAR_WIDTH,
                }}
              >
                <div
                  style={{
                    width: '100%',
                    height: NO_DATA_HEIGHT,
                    borderRadius: '6px',
                    background:
                      'repeating-linear-gradient(45deg, #e2e8f0, #e2e8f0 4px, #f1f5f9 4px, #f1f5f9 8px)',
                    border: '1px solid #e2e8f0',
                    opacity: isHovered ? 0.9 : 0.7,
                    transition: 'opacity 0.2s ease',
                  }}
                />
              </div>
            )}

            <div
              style={{
                marginTop: '8px',
                fontSize: '0.68rem',
                fontWeight: 600,
                color: !hasData ? '#cbd5e1' : isHovered ? '#1e40af' : '#64748b',
                textAlign: 'center',
                transition: 'color 0.2s ease',
              }}
            >
              {ROLE_LABELS[e.role] ?? e.role}
            </div>
          </div>
        );
      })}

      {/* Tooltip is portaled to document.body and positioned with `fixed`
          coords from the hovered bar's own getBoundingClientRect() —
          NOT rendered inline inside this row. This row scrolls
          horizontally (overflowX: 'auto'), and per the CSS overflow
          spec, once one axis is set to anything but 'visible', the
          computed value of the OTHER axis becomes 'auto' too — so even
          though overflowY is declared 'visible' above, a tall inline
          tooltip (up to 5 lines: 4 segments + a total line) that needs
          more room than the 20px paddingTop reserves gets hard-clipped
          at the container's top edge. Portaling escapes that entirely. */}
      {hovered &&
        hoveredEntry &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              left: hovered.rect.left + hovered.rect.width / 2,
              top: hovered.rect.top - 14,
              transform: 'translate(-50%, -100%)',
              background: '#ffffff',
              borderRadius: '10px',
              padding: '8px 12px',
              boxShadow: '0 8px 20px rgba(15, 23, 42, 0.18)',
              border: '1px solid #e2e8f0',
              whiteSpace: 'nowrap',
              zIndex: 9999,
              pointerEvents: 'none',
            }}
          >
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>
              {ROLE_LABELS[hoveredEntry.role] ?? hoveredEntry.role}
            </div>
            {hoveredHasData ? (
              <>
                {hoveredSegments.map((s) =>
                  s.value > 0 ? (
                    <div key={s.key} style={{ fontSize: '0.68rem', fontWeight: 600, color: s.textColor }}>
                      {s.label}: {s.value}
                    </div>
                  ) : null
                )}
                <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#1e293b', marginTop: '2px' }}>
                  Total: {hoveredEntry.total}
                </div>
              </>
            ) : (
              <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#94a3b8' }}>No data recorded</div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

// ==================== CLASS COVERAGE DONUT (ECharts) ====================
// Rebuilt on ECharts, matching FeesSummary's EChartsPieChart recipe: per-slice
// linear gradients, rounded/white-bordered slices with a soft drop shadow, a
// styled tooltip, and a legend that spells out the count/percentage rather than
// just the label. The center total (previously drawn by hand in the SVG version)
// is now an ECharts `graphic` overlay instead of raw SVG <text>.
const ClassCoverageDonut: React.FC<{
  data: ClassCoverageStats | null;
  loading: boolean;
  error: string | null;
}> = ({ data, loading, error }) => {
  const chartRef = useRef<HTMLDivElement>(null);

  const total = data?.total_classes ?? 0;
  const hasData = !!data && total > 0;

  useEffect(() => {
    if (!chartRef.current || !hasData || !data) return;

    const chart = echarts.init(chartRef.current);

    const segments: { key: CoverageKey; value: number }[] = [
      { key: 'unaffected', value: data.unaffected_classes },
      { key: 'pending', value: data.pending_confirmation_classes },
      { key: 'affected', value: data.affected_classes },
    ];

    // Ring stays anchored near the left of the canvas; the legend gets
    // everything to the right of it. Canvas width below (COVERAGE_CHART_MAX_WIDTH)
    // is sized with enough margin that "Pending Confirmation: 132 (98%)" — the
    // longest legend line — never runs past the canvas edge and gets clipped.
    const w = chartRef.current.clientWidth;
    const h = chartRef.current.clientHeight;
    const centerX = w * 0.26;
    const centerY = h * 0.5;

    const option = {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        borderRadius: 12,
        textStyle: { color: '#1e293b', fontSize: 12, fontWeight: '600' },
        extraCssText: 'box-shadow: 0 8px 32px rgba(59, 130, 246, 0.2);',
        padding: [8, 12],
      },
      legend: {
        orient: 'vertical',
        left: '44%',
        top: 'middle',
        itemGap: 14,
        itemWidth: 10,
        itemHeight: 10,
        icon: 'circle',
        textStyle: { color: '#475569', fontSize: 11.5, fontWeight: '600' },
        formatter: (name: string) => {
          const seg = segments.find((s) => COVERAGE_STYLE[s.key].label === name);
          const pct = seg && total > 0 ? Math.round((seg.value / total) * 100) : 0;
          return `${name}: ${seg?.value ?? 0} (${pct}%)`;
        },
      },
      series: [
        {
          type: 'pie',
          // Radius bumped up ~10% from the earlier ['40%','58%'].
          radius: ['44%', '64%'],
          center: [centerX, centerY],
          avoidLabelOverlap: true,
          label: { show: false },
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 3,
            shadowBlur: 12,
            shadowColor: 'rgba(15, 23, 42, 0.15)',
          },
          emphasis: {
            itemStyle: { shadowBlur: 20, shadowColor: 'rgba(15, 23, 42, 0.25)' },
            scaleSize: 6,
          },
          data: segments.map((seg) => ({
            value: seg.value,
            name: COVERAGE_STYLE[seg.key].label,
            itemStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: COVERAGE_STYLE[seg.key].colorStops,
              },
            },
          })),
        },
      ],
    };

    chart.setOption(option);
    // centerX/centerY above are fixed pixel values computed once on init, so
    // a plain chart.resize() would rescale the ring while leaving the pie's
    // center stale relative to the new canvas size. Recompute on resize too.
    const handleResize = () => {
      if (!chartRef.current) return;
      const newW = chartRef.current.clientWidth;
      const newH = chartRef.current.clientHeight;
      chart.setOption({ series: [{ center: [newW * 0.26, newH * 0.5] }] });
      chart.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.dispose();
    };
  }, [data, hasData, total]);

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          minHeight: 180,
        }}
      >
        <style>{`@keyframes coverageSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)',
          }}
        >
          <i className="pi pi-sync" style={{ color: 'white', fontSize: '1.1rem', animation: 'coverageSpin 1s linear infinite' }}></i>
        </div>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: '#64748b' }}>Loading coverage...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          minHeight: 180,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          color: '#dc2626',
          fontSize: 12.5,
          fontWeight: 500,
          padding: '0 12px',
        }}
      >
        {error}
      </div>
    );
  }

  if (!hasData) {
    return (
      <div
        style={{
          minHeight: 180,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          color: '#94a3b8',
          padding: '0 12px',
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: '#eff6ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <i className="pi pi-th-large" style={{ fontSize: '1.15rem', color: '#93c5fd' }}></i>
        </div>
        <div style={{ fontSize: 12, fontWeight: 500, textAlign: 'center' }}>
          No scheduled classes found for this period.
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: 460, margin: '0 auto' }}>
      <div ref={chartRef} style={{ width: '100%', height: 178 }} />
      {/* Anchored to the same 26%-of-width x-position the pie's `center` uses
          above (marginLeft: 26% + translateX(-50%) centers the text ON that
          point), not centered in the whole widget — otherwise it drifts away
          from wherever the ring actually sits. */}
      <div
        style={{
          width: 'max-content',
          marginLeft: '26%',
          transform: 'translateX(-50%)',
          textAlign: 'center',
          marginTop: 2,
        }}
      >
        <div style={{ fontSize: 20, fontWeight: 800, color: '#1e293b', lineHeight: 1.15 }}>{total}</div>
        <div style={{ fontSize: 9.5, fontWeight: 600, color: '#94a3b8' }}>Classes</div>
      </div>
    </div>
  );
};

// ==================== MAIN CARD ====================
const StaffAttendanceStatsCard: React.FC = () => {
  const [period, setPeriod] = useState<PeriodKey>('today');
  const [role, setRole] = useState<RoleKey>('');
  const [scanType, setScanType] = useState<ScanType>('checkin');
  const [animated, setAnimated] = useState(false);

  const { data, loading, error } = useStaffAttendanceStats(period, role);
  const { data: coverage, loading: coverageLoading, error: coverageError } = useClassCoverageStats(period, role);

  useEffect(() => {
    setAnimated(false);
    const t = setTimeout(() => setAnimated(true), 50);
    return () => clearTimeout(t);
  }, [data, scanType]);

  const hasAnyData = !!data && (data.summary.checkin.total > 0 || data.summary.checkout.total > 0);
  const activeScanStats = data ? data.summary[scanType] : null;

  return (
    <Card
      style={{
        borderRadius: '16px',
        marginBottom: '24px',
        border: '1px solid rgba(59, 130, 246, 0.15)',
        background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
      }}
    >
      {/* Header — title on the left, Today/Week pill switcher on the right */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: '14px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 6px 16px rgba(59, 130, 246, 0.25)',
            }}
          >
            <i className="pi pi-id-card" style={{ color: 'white', fontSize: '0.9rem' }}></i>
          </div>
          <h6 style={{ color: '#1e40af', fontWeight: 700, margin: 0, fontSize: '0.95rem' }}>
            Staff Attendance & Class Coverage
          </h6>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 2,
            background: '#f1f5f9',
            borderRadius: 999,
            padding: 3,
            border: '1px solid #e2e8f0',
          }}
        >
          {(['today', 'week'] as PeriodKey[]).map((key) => {
            const isActive = period === key;
            return (
              <button
                key={key}
                onClick={() => setPeriod(key)}
                style={{
                  padding: '5px 12px',
                  borderRadius: 999,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 700,
                  background: isActive ? '#ffffff' : 'transparent',
                  color: isActive ? '#1e40af' : '#94a3b8',
                  boxShadow: isActive ? '0 1px 4px rgba(30, 64, 175, 0.15)' : 'none',
                  transition: 'background 0.2s ease, box-shadow 0.2s ease',
                }}
              >
                {PERIOD_LABELS[key]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Role filter row — applies to both the bar chart and the coverage donut */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        {ROLE_OPTIONS.map((opt) => {
          const isActive = role === opt.key;
          return (
            <button
              key={opt.key || 'all'}
              onClick={() => setRole(opt.key)}
              style={{
                padding: '5px 12px',
                borderRadius: 999,
                border: `1px solid ${isActive ? '#3b82f6' : '#e2e8f0'}`,
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 700,
                background: isActive ? 'rgba(59, 130, 246, 0.08)' : 'white',
                color: isActive ? '#1e40af' : '#64748b',
                transition: 'background 0.2s ease, border-color 0.2s ease',
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {/* Two-column body: attendance bars on the left, class coverage on the right */}
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'stretch' }}>
        {/* ── Left: staff attendance bars ── */}
        <div style={{ flex: '1 1 340px', minWidth: 280 }}>
          {loading ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                padding: '36px 0',
              }}
            >
              <style>{`
                @keyframes staffStatsSpin {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
              `}</style>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)',
                }}
              >
                <i
                  className="pi pi-sync"
                  style={{ color: 'white', fontSize: '1.1rem', animation: 'staffStatsSpin 1s linear infinite' }}
                ></i>
              </div>
              <div style={{ color: '#64748b', fontSize: 13, fontWeight: 600 }}>Loading attendance stats...</div>
            </div>
          ) : error ? (
            <div style={{ padding: '24px 0', textAlign: 'center', color: '#dc2626', fontSize: 13, fontWeight: 500 }}>
              {error}
            </div>
          ) : !hasAnyData ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                padding: '32px 8px',
                color: '#94a3b8',
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: '#eff6ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className="pi pi-id-card" style={{ fontSize: '1.15rem', color: '#93c5fd' }}></i>
              </div>
              <div style={{ fontSize: 12, fontWeight: 500 }}>
                No attendance scans recorded {period === 'today' ? 'today' : 'in the last 7 days'}
                {role ? ` for ${ROLE_LABELS[role] ?? role}` : ''}.
              </div>
            </div>
          ) : (
            <>
              {/* Legend + Check-in/Check-out toggle + total */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 12,
                  marginBottom: 6,
                }}
              >
                <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                  {SEGMENT_ORDER.map((k) => (
                    <div key={k} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ width: 10, height: 10, borderRadius: 3, background: SEGMENT_STYLE[k].idleGradient }} />
                      <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569' }}>
                        {SEGMENT_STYLE[k].label}
                      </span>
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: 2,
                    background: '#f1f5f9',
                    borderRadius: 999,
                    padding: 3,
                    border: '1px solid #e2e8f0',
                  }}
                >
                  {(['checkin', 'checkout'] as ScanType[]).map((st) => {
                    const isActive = scanType === st;
                    return (
                      <button
                        key={st}
                        onClick={() => setScanType(st)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 5,
                          padding: '4px 10px',
                          borderRadius: 999,
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 11,
                          fontWeight: 700,
                          background: isActive ? '#ffffff' : 'transparent',
                          color: isActive ? '#1e40af' : '#94a3b8',
                          boxShadow: isActive ? '0 1px 4px rgba(30, 64, 175, 0.15)' : 'none',
                        }}
                      >
                        <i className={`pi ${st === 'checkin' ? 'pi-sign-in' : 'pi-sign-out'}`} style={{ fontSize: 10 }}></i>
                        {st === 'checkin' ? 'Check-in' : 'Check-out'}
                        {activeScanStats && (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 800,
                              color: isActive ? '#1e40af' : '#94a3b8',
                            }}
                          >
                            {data!.summary[st].total}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <RoleStatusBars byRole={data!.by_role} scanType={scanType} roleFilter={role} animated={animated} />
            </>
          )}
        </div>

        {/* Divider between the two panels on wide layouts */}
        <div style={{ width: 1, background: '#e2e8f0', alignSelf: 'stretch', minHeight: 190 }} />

        {/* ── Right: class coverage donut ── */}
        <div style={{ flex: '1 1 400px', minWidth: 400, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e40af', marginBottom: 10, alignSelf: 'flex-start' }}>
            Class Coverage {PERIOD_LABELS[period]}
          </div>
          <ClassCoverageDonut data={coverage} loading={coverageLoading} error={coverageError} />
        </div>
      </div>
    </Card>
  );
};

export default StaffAttendanceStatsCard;